"use client";

import { datadogLogs } from "@datadog/browser-logs";
import { getBuildVersion } from "@frontend/lib/app-version";
import { useCallback, useEffect, useRef } from "react";
import { useIsKiosk } from "./useIsKiosk";

/** Returns a concise browser label, e.g. "Chrome 120", "Safari 17", "Edge 120". */
function parseBrowser(ua: string): string {
  const major = (m: RegExpMatchArray | null) => m?.[1]?.split(".")[0] ?? "";
  const edge = ua.match(/Edg\/(\S+)/);
  if (edge) return `Edge ${major(edge)}`;
  const samsung = ua.match(/SamsungBrowser\/(\S+)/);
  if (samsung) return `Samsung ${major(samsung)}`;
  const firefox = ua.match(/Firefox\/(\S+)/);
  if (firefox) return `Firefox ${major(firefox)}`;
  const chrome = ua.match(/Chrome\/(\S+)/);
  if (chrome) return `Chrome ${major(chrome)}`;
  // Safari: use Version/x.x rather than the Safari/xxx build token
  const safari = ua.match(/Version\/(\S+)/);
  if (safari && ua.includes("Safari")) return `Safari ${major(safari)}`;
  return ua.split(" ")[0] ?? ua; // fallback: first token
}

// Mirrors timing constants from useDesktopHeartbeat.ts in hex-bridge
const HEARTBEAT_INTERVAL_MS = 60_000;
const HEARTBEAT_JITTER_MS = 5_000;
const HEARTBEAT_MIN_INTERVAL_MS = 30_000; // 30s — minimum gap between sends of the same type

type HeartbeatType =
  | "periodic"
  | "login"
  | "resume"
  | "terminal_change"
  | "standby_change"
  | "logout"
  | "permission_change";

export interface KioskHeartbeatParams {
  kioskId: string | null;
  orgId: number | null;
  stripeTerminalConnected: boolean;
  stripeTerminalId: string | null;
  stripeTerminalLabel: string | null;
  stripeTerminalLocationId: string | null;
  /** reader.action.status — "in_progress" | "succeeded" | "failed" | null */
  stripeTerminalActionStatus: string | null;
  /** Whether the kiosk is showing the standby/screensaver state. */
  kioskDisplayState: "active" | "standby";
  /** Device settings from the kiosk settings UI — included in monitor POST only, not Datadog. */
  kioskSettings: {
    kioskName: string | null;
    kioskLocation: string | null;
    practiceId: string | null;
    ehrLocationId: string | null;
    qrMode: boolean | null;
    inactivityTimeoutMs: number | null;
    navigationMode: "stateMachine" | "window" | null;
    permissionRequestMode: "startup" | "onDemand" | null;
  };
}

/**
 * Emits periodic heartbeat logs for provisioned kiosk devices.
 *
 * Two delivery paths on every send:
 *   1. datadogLogs.logger.info() — authoritative record in Datadog
 *   2. POST /api/kiosk/fleet-heartbeat (same-origin BFF) — forwards to hex-monitor
 *      with server-side secrets; bypasses DD's ~5 min indexing delay so the machine
 *      appears on the fleet dashboard immediately. Fire-and-forget; failures are silent.
 *
 * Only fires when kiosk_generated_machine_id cookie is present (set by
 * proxy.ts on first load of the device's launch URL - only on kiosk subdomain for non-qr mode sessions).
 *
 * Cadence: 60s ± 5s jitter, minimum 30s between any two sends.
 * Lifecycle triggers: immediate on mount (login), immediate on visibility resume.
 */
export function useKioskHeartbeat(params: KioskHeartbeatParams) {
  const { isKiosk, machineId, ipAddress } = useIsKiosk();
  const lastSentByTypeRef = useRef<Partial<Record<HeartbeatType, number>>>({});
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Keep params in a ref so periodic ticks always use the latest values
  // without needing to restart the timer when terminal state changes.
  const paramsRef = useRef(params);
  useEffect(() => {
    paramsRef.current = params;
  });

  const sendHeartbeat = useCallback(
    (heartbeatType: HeartbeatType) => {
      const now = Date.now();
      const lastSent = lastSentByTypeRef.current[heartbeatType] ?? 0;
      if (now - lastSent < HEARTBEAT_MIN_INTERVAL_MS) return; // dedup rapid fires per type — a periodic send never blocks other types
      lastSentByTypeRef.current[heartbeatType] = now;

      const p = paramsRef.current;
      const appVersion = getBuildVersion();
      const platform = parseBrowser(navigator.userAgent);

      const environment = {
        machineId,
        version: appVersion,
        platform,
        localIp: ipAddress,
      };
      // Path 1: Datadog — authoritative log record
      // DD browser SDK cannot flush during beforeunload — logout is covered by the keepalive fetch to hex-monitor
      if (heartbeatType !== "logout") {
        datadogLogs.logger.info("kiosk heartbeat", {
          context: {
            action: "kiosk_heartbeat",
            metadata: {
              heartbeatType,
              orgId: p.orgId,
              adminId: null,
              adminEmail: null,
              kioskDisplayState: p.kioskDisplayState,
              cameraPermission: cameraPermissionRef.current,
              stripeTerminalConnected: p.stripeTerminalConnected,
              stripeTerminalId: p.stripeTerminalId,
              stripeTerminalLabel: p.stripeTerminalLabel,
              stripeTerminalLocationId: p.stripeTerminalLocationId,
              stripeTerminalActionStatus: p.stripeTerminalActionStatus,
            },
          },
          environment,
        });
      }

      // Path 2: same-origin proxy → hex-monitor (secrets + Vercel bypass live on server only)
      if (machineId) {
        fetch("/api/kiosk/fleet-heartbeat", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            machineId,
            source: "kiosk-web",
            kioskId: p.kioskId,
            adminId: null,
            adminEmail: null,
            orgId: p.orgId,
            overlayVisible: null,
            heartbeatType,
            kioskDisplayState: p.kioskDisplayState,
            cameraPermission: cameraPermissionRef.current,
            stripeTerminalConnected: p.stripeTerminalConnected,
            stripeTerminalId: p.stripeTerminalId,
            stripeTerminalLabel: p.stripeTerminalLabel,
            stripeTerminalLocationId: p.stripeTerminalLocationId,
            appVersion,
            platform,
            arch: null,
            memoryFreeMB: null,
            ipAddress,
            kioskSettings: p.kioskSettings,
          }),
          keepalive: true,
        }).catch(() => {
          // Intentionally silent — DD is the authoritative path
        });
      } else {
        console.error("heartbeat: cannot send to monitor (no machineId)");
      }
    },
    [machineId, ipAddress],
  ); // machineId and ipAddress are the only external values; params are read via ref

  const scheduleNext = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    const jitter = (Math.random() * 2 - 1) * HEARTBEAT_JITTER_MS;
    timerRef.current = setTimeout(() => {
      sendHeartbeat("periodic");
      scheduleNext();
    }, HEARTBEAT_INTERVAL_MS + jitter);
  }, [sendHeartbeat]);

  // Fire login heartbeat on mount and start periodic schedule.
  // Re-runs only if isKiosk changes (after the identity query resolves).
  useEffect(() => {
    if (!isKiosk) return;
    sendHeartbeat("login");
    scheduleNext();
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [isKiosk, sendHeartbeat, scheduleNext]);

  // Fire resume heartbeat when the tab/app becomes visible again.
  useEffect(() => {
    if (!isKiosk) return;
    const handleVisibilityChange = () => {
      if (!document.hidden) sendHeartbeat("resume");
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [isKiosk, sendHeartbeat]);

  // Fire standby_change heartbeat when the kiosk enters or exits the standby screen.
  // Skip initial render to avoid doubling the login heartbeat on mount.
  const lastDisplayStateRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!isKiosk) return;
    if (lastDisplayStateRef.current === undefined) {
      lastDisplayStateRef.current = params.kioskDisplayState;
      return;
    }
    if (params.kioskDisplayState !== lastDisplayStateRef.current) {
      lastDisplayStateRef.current = params.kioskDisplayState;
      sendHeartbeat("standby_change");
    }
  }, [isKiosk, params.kioskDisplayState, sendHeartbeat]);

  // Fire terminal_change heartbeat when any meaningful reader state changes:
  // connect/disconnect (id), online/offline (connected), or action state changes
  // (in_progress, failed, succeeded — covers payment/setup intent lifecycle).
  // Skip the initial render to avoid doubling the login heartbeat on mount.
  const terminalFingerprint = `${params.stripeTerminalId}|${params.stripeTerminalConnected}|${params.stripeTerminalActionStatus}`;
  const lastTerminalFingerprintRef = useRef<string | undefined>(undefined);
  const cameraPermissionRef = useRef<PermissionState | null>(null);

  useEffect(() => {
    if (!isKiosk) return;
    if (lastTerminalFingerprintRef.current === undefined) {
      // First render after isKiosk resolves — record baseline, don't fire
      lastTerminalFingerprintRef.current = terminalFingerprint;
      return;
    }
    if (terminalFingerprint !== lastTerminalFingerprintRef.current) {
      lastTerminalFingerprintRef.current = terminalFingerprint;
      sendHeartbeat("terminal_change");
    }
  }, [isKiosk, terminalFingerprint, sendHeartbeat]);

  // Watch camera permission state and fire permission_change heartbeat on transitions.
  useEffect(() => {
    if (!isKiosk) return;
    if (typeof navigator === "undefined" || !navigator.permissions) return;

    let ps: PermissionStatus | null = null;

    navigator.permissions
      .query({ name: "camera" as PermissionName })
      .then((status) => {
        ps = status;
        cameraPermissionRef.current = status.state;
        status.onchange = () => {
          cameraPermissionRef.current = status.state;
          sendHeartbeat("permission_change");
        };
      })
      .catch(() => {});

    return () => {
      if (ps) ps.onchange = null;
    };
  }, [isKiosk, sendHeartbeat]);

  // Fire logout heartbeat when the page is about to unload (tab close, navigation away).
  // Uses keepalive fetch on the same-origin fleet-heartbeat proxy path.
  useEffect(() => {
    if (!isKiosk) return;
    const handleBeforeUnload = () => {
      sendHeartbeat("logout");
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isKiosk, sendHeartbeat]);
}
